import React from "react";
import { WHATSAPP_URL, CLINIC } from "@/lib/constants";

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

const WhatsAppIcon = ({ className = "h-4 w-4" }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className={className}
        aria-hidden="true"
    >
        <path d="M.057 24l1.687-6.163A11.867 11.867 0 0 1 .14 11.892C.144 5.335 5.481 0 12.039 0a11.82 11.82 0 0 1 8.413 3.488 11.83 11.83 0 0 1 3.48 8.414c-.003 6.557-5.34 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.449L.057 24zM6.6 20.13a9.86 9.86 0 0 0 5.446 1.601h.005c5.448 0 9.886-4.434 9.889-9.885a9.83 9.83 0 0 0-2.892-6.99 9.82 9.82 0 0 0-6.991-2.899c-5.452 0-9.887 4.434-9.889 9.885a9.86 9.86 0 0 0 1.512 5.26l.235.374-1 3.648 3.749-.984.187.111zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.297-.495.099-.198.05-.371-.025-.52-.074-.149-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.371-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
    </svg>
);

const FooterCta = () => (
    <div className="border-b border-white/10">
        <div className="container-luxe grid grid-cols-1 items-center gap-10 py-16 md:grid-cols-12 md:py-20">
            <div className="md:col-span-7 lg:col-span-8">
                <p className="font-sans text-[11px] uppercase tracking-widestx text-gold-400">
                    Sua consulta começa aqui
                </p>
                <h3 className="mt-4 font-serif text-3xl font-medium leading-tight text-white text-balance md:text-4xl lg:text-[42px]">
                    Odontologia de precisão para quem não aceita menos que
                    a <span className="italic text-gold-300">excelência</span>.
                </h3>
                <p className="mt-4 max-w-xl font-sans text-sm font-light leading-relaxed text-white/60 md:text-base">
                    Atendimento premium e agenda restrita. Reserve seu
                    horário com o Dr. André Oliva e tenha uma experiência
                    de reabilitação com padrão internacional.
                </p>
            </div>
            <div className="md:col-span-5 md:text-right lg:col-span-4">
                <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-whatsapp-cta"
                    className="group relative inline-flex items-center justify-center gap-3 overflow-hidden bg-gold-500 px-8 py-5 font-sans text-xs font-semibold uppercase tracking-widestx text-white transition-all duration-500 hover:bg-gold-600 md:text-[13px]"
                >
                    <WhatsAppIcon className="relative z-10 h-4 w-4" />
                    <span className="relative z-10">
                        Agendar via WhatsApp
                    </span>
                    <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
                </a>
            </div>
        </div>
    </div>
);

const FooterAbout = () => (
    <div className="md:col-span-4">
        <div className="flex flex-col leading-none">
            <span className="font-serif text-lg uppercase tracking-widestx text-white md:text-xl">
                Oliva Implantes
            </span>
            <span className="mt-3 inline-block h-px w-10 bg-gold-500" />
        </div>
        <p className="mt-6 max-w-sm font-sans text-sm font-light leading-relaxed text-white/55">
            Excelência em Reabilitação Oral e Implantes Dentários. Clínica
            boutique especializada em implantodontia e reabilitação oral
            de alta complexidade — cada sorriso, um projeto único.
        </p>
    </div>
);

const FooterAddress = () => (
    <div className="md:col-span-4">
        <h4 className="font-sans text-[11px] uppercase tracking-widestx text-gold-400">
            Endereço
        </h4>
        <address className="mt-4 not-italic font-sans text-sm leading-relaxed text-white/75">
            {CLINIC.addressLine1}
            <br />
            {CLINIC.addressLine2}
            <br />
            {CLINIC.addressLine3}
        </address>
        <p className="mt-3 font-sans text-xs leading-relaxed text-white/50">
            Atendimento somente com hora marcada
        </p>
    </div>
);

const FooterContact = () => (
    <div className="md:col-span-4">
        <h4 className="font-sans text-[11px] uppercase tracking-widestx text-gold-400">
            Contato
        </h4>
        <ul className="mt-4 space-y-2 font-sans text-sm text-white/75">
            <li>
                <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-colors hover:text-gold-300"
                >
                    {CLINIC.phoneDisplay}
                </a>
            </li>
            <li>
                <a
                    href={`mailto:${CLINIC.email}`}
                    className="transition-colors hover:text-gold-300"
                >
                    {CLINIC.email}
                </a>
            </li>
            <li className="pt-2">
                <a
                    href="#top"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widestx text-white/55 transition-colors hover:text-gold-300"
                >
                    Voltar ao topo
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        className="h-3 w-3"
                        aria-hidden="true"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 19V5M5 12l7-7 7 7"
                        />
                    </svg>
                </a>
            </li>
        </ul>
    </div>
);

const FooterBottom = ({ year }) => (
    <div className="border-t border-white/10">
        <div className="container-luxe flex flex-col items-start justify-between gap-3 py-6 text-[11px] uppercase tracking-widestx text-white/40 md:flex-row md:items-center md:py-7">
            <p>
                © {year} {CLINIC.name}. Todos os direitos reservados.
            </p>
            <p className="normal-case tracking-normal text-white/35">
                Este site possui caráter informativo, conforme o Código de
                Ética Odontológico.
            </p>
        </div>
    </div>
);

/* -------------------------------------------------------------------------- */
/*  Footer                                                                    */
/* -------------------------------------------------------------------------- */

const Footer = () => {
    const year = new Date().getFullYear();

    return (
        <footer
            data-testid="site-footer"
            className="relative bg-onyx text-white"
        >
            <FooterCta />

            <div className="container-luxe grid grid-cols-1 gap-12 py-16 md:grid-cols-12 md:gap-10 md:py-20">
                <FooterAbout />
                <FooterAddress />
                <FooterContact />
            </div>

            <FooterBottom year={year} />
        </footer>
    );
};

export default Footer;
